import { ExternalLink, Eye, DollarSign, Calendar, Image, Video, FileText, Tag } from 'lucide-react'

interface AdAnalysis {
  messagingTheme?: string
  targetPersona?: string
  emotionalAppeal?: string
  keyValueProps?: string[]
  adCategory?: string
  copyQualityScore?: number
  ctaEffectiveness?: number
}

interface CompetitorAd {
  id: string
  platform: 'GOOGLE' | 'META' | 'TIKTOK' | 'LINKEDIN'
  format: 'TEXT' | 'IMAGE' | 'VIDEO' | 'CAROUSEL' | 'RESPONSIVE'
  status: 'ACTIVE' | 'INACTIVE' | 'UNKNOWN'
  headline?: string
  bodyText?: string
  ctaText?: string
  destinationUrl?: string
  thumbnailUrl?: string
  imageUrls?: string[]
  impressionsMin?: number
  impressionsMax?: number
  spendMin?: number
  spendMax?: number
  targetRegions?: string[]
  firstSeenAt: string
  lastSeenAt: string
  analysis?: AdAnalysis
}

interface AdCardProps {
  ad: CompetitorAd
  onClick?: () => void
}

const platformColors: Record<string, { bg: string; text: string }> = {
  GOOGLE: { bg: 'bg-blue-100', text: 'text-blue-700' },
  META: { bg: 'bg-purple-100', text: 'text-purple-700' },
  TIKTOK: { bg: 'bg-pink-100', text: 'text-pink-700' },
  LINKEDIN: { bg: 'bg-sky-100', text: 'text-sky-700' }
}

const formatIcons: Record<string, any> = {
  TEXT: FileText,
  IMAGE: Image,
  VIDEO: Video,
  CAROUSEL: Image,
  RESPONSIVE: FileText
}

export default function AdCard({ ad, onClick }: AdCardProps) {
  const formatSpend = (min?: number, max?: number) => {
    if (!min && !max) return null
    if (min === max) return `$${min?.toLocaleString()}`
    return `$${(min || 0).toLocaleString()} - $${(max || 0).toLocaleString()}`
  }

  const formatImpressions = (min?: number, max?: number) => {
    if (!min && !max) return null
    if (min === max) return min?.toLocaleString()
    return `${(min || 0).toLocaleString()} - ${(max || 0).toLocaleString()}`
  }

  const platformStyle = platformColors[ad.platform] || { bg: 'bg-gray-100', text: 'text-gray-700' }
  const FormatIcon = formatIcons[ad.format] || FileText
  const spend = formatSpend(ad.spendMin, ad.spendMax)
  const impressions = formatImpressions(ad.impressionsMin, ad.impressionsMax)

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer group"
    >
      {/* Header: Platform & Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${platformStyle.bg} ${platformStyle.text}`}>
            {ad.platform}
          </span>
          <span className="text-gray-400">
            <FormatIcon className="h-4 w-4" />
          </span>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          ad.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
          ad.status === 'INACTIVE' ? 'bg-gray-100 text-gray-600' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {ad.status}
        </span>
      </div>

      {/* Creative Preview */}
      {ad.thumbnailUrl && (
        <div className="relative mb-3 rounded-lg overflow-hidden bg-gray-100">
          <img
            src={ad.thumbnailUrl}
            alt="Ad creative"
            className="w-full h-40 object-cover group-hover:scale-105 transition-transform"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
          {ad.format === 'VIDEO' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Video className="h-6 w-6 text-gray-800" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ad Copy */}
      <h4 className="font-semibold text-gray-900 mb-1 line-clamp-2 text-sm">
        {ad.headline || 'No headline'}
      </h4>
      {ad.bodyText && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {ad.bodyText}
        </p>
      )}

      {/* CTA */}
      {ad.ctaText && (
        <div className="mb-3">
          <span className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
            {ad.ctaText}
          </span>
        </div>
      )}

      {/* Metrics Row */}
      <div className="flex items-center gap-4 text-xs text-gray-500 border-t pt-3">
        {spend && (
          <div className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            <span>{spend}</span>
          </div>
        )}
        {impressions && (
          <div className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            <span>{impressions}</span>
          </div>
        )}
        {!spend && !impressions && (
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>
              {new Date(ad.firstSeenAt).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      {/* AI Insights */}
      {ad.analysis && (ad.analysis.messagingTheme || ad.analysis.targetPersona) && (
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center gap-1 mb-2">
            <Tag className="h-3 w-3 text-gray-400" />
            <span className="text-xs text-gray-500">AI Insights</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {ad.analysis.messagingTheme && (
              <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                {ad.analysis.messagingTheme}
              </span>
            )}
            {ad.analysis.targetPersona && (
              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs truncate max-w-[150px]">
                {ad.analysis.targetPersona}
              </span>
            )}
          </div>
        </div>
      )}

      {/* View Link (on hover) */}
      {ad.destinationUrl && (
        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <a
            href={ad.destinationUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 py-1"
          >
            View Landing Page <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  )
}
